import * as React from 'react'
import { Node } from '@tiptap/core'
import {
  NodeViewWrapper,
  NodeViewProps,
  ReactNodeViewRenderer,
  NodeViewContent,
} from '@tiptap/react'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  Pencil1Icon,
  ChatBubbleIcon,
  GlobeIcon,
  CubeIcon,
  ImageIcon,
  PersonIcon,
  Cross1Icon,
  CommitIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { ErrorBoundary } from 'react-error-boundary'
import { LightSuggestion, Suggestion } from './components/suggestion-box'
import { BranchIcon, MergeIcon } from '~/components/icons/branch-icon'
import Link from 'next/link'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'

// Component to render AI Tool node
export const AiToolNodeView: React.FC<NodeViewProps> = ({ node, selected }) => {
  return (
    <NodeViewWrapper
      className="relative mb-4 tool-container"
      data-node-view-wrapper
      data-node-selected={selected}
    >
      <div className="ai-tool" data-tool-id={node.attrs.toolId}>
        <NodeViewContent />
      </div>
    </NodeViewWrapper>
  )
}

// Component to render AI Tool Call node
export const AiToolCallNodeView: React.FC<NodeViewProps> = ({ node }) => {
  const { teamSlug, repoSlug } = useDashboardSlugs()
  let label = node.attrs.toolName
  let icon = <MagnifyingGlassIcon />
  let overrideExpandTextContent = null
  let noop = false
  switch (node.attrs.toolName) {
    case 'get_content_structure':
    case 'getRepositoryStructure':
      label = 'Getting repository structure'
      icon = <CubeIcon />
      break
    case 'get_example_content_structure':
    case 'getExampleRepositoryStructure':
      label = `Checking out example repository "${node.attrs.args.handle}"`
      icon = <CubeIcon />
      break
    case 'createImage':
      label = `Creating image`
      icon = <ImageIcon />
      break
    case 'editImage':
      label = `Editing image`
      icon = <ImageIcon />
      break
    case 'getUserInfo':
      label = `Getting user info`
      icon = <PersonIcon />
      break
    case 'query_content':
    case 'queryContentRepository':
      label = 'Querying repository'
      icon = <MagnifyingGlassIcon />
      break
    case 'suggestContentAndOrSchemaChanges':
      label = 'Suggesting changes'
      icon = <Pencil1Icon />
      noop = true
      break
    case 'commit':
      label = 'Commit'
      icon = <CommitIcon />
      break
    case 'checkout_branch':
      label = (
        <>
          Checking out branch{' '}
          <Link
            href={`/${teamSlug}/${repoSlug}/branch/${encodeURIComponent(node.attrs.args.branchName)}`}
          >
            "{node.attrs.args.branchName}"
          </Link>
        </>
      )
      icon = <BranchIcon />
      break
    case 'create_branch':
      label = (
        <>
          Creating branch{' '}
          <Link
            href={`/${teamSlug}/${repoSlug}/branch/${encodeURIComponent(node.attrs.args.branchName)}`}
          >
            "{node.attrs.args.branchName}"
          </Link>
        </>
      )
      icon = <BranchIcon />
      break
    case 'list_branches':
      label = 'Listing branches'
      icon = <BranchIcon />
      break
    case 'get_current_ref':
      label = 'Getting current branch'
      icon = <BranchIcon />
      break
    case 'merge_branch':
      label = `Merging branch "${node.attrs.args.branchName}"`
      icon = <MergeIcon />
      break
    case 'update_blocks':
    case 'delete_blocks':
    case 'create_blocks':
      label = node.attrs.toolName
        .replace('_', ' ')
        .replace(/^\w/, (c: string) => c.toUpperCase())
      icon = <Pencil1Icon />
      break
    case 'think':
      label = `Thinking`
      icon = <ChatBubbleIcon />
      overrideExpandTextContent = node.attrs.args.thought
      break
    case 'searchTheWeb':
      label = `Searching the web for "${node.attrs.args.query}"`
      icon = <MagnifyingGlassIcon />
      break
    case 'crawlWebsite':
      label = `Crawling website "${node.attrs.args.url}"`
      icon = <GlobeIcon />
      break
    default:
      break
  }

  if (noop) return null
  return (
    <NodeViewWrapper>
      <button
        className="text-dark-faint text-left flex items-start gap-1 font-medium cursor-default group/toolresult hover:text-dark-muted"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          const container = e.currentTarget.closest('.tool-container')
          container
            ?.querySelectorAll('.ai-tool-toggle-hidden')
            .forEach((el) => {
              el.classList.toggle('hidden')
            })
          if (overrideExpandTextContent) {
            const trEl = container?.querySelector('[data-tool-result]')
            if (trEl) {
              trEl.textContent = overrideExpandTextContent
            }
          }
        }}
      >
        <span className="mt-0.5">
          <span className="hidden ai-tool-toggle-hidden">
            <ChevronDownIcon />
          </span>
          <span className="ai-tool-toggle-hidden">
            <span className="group-hover/toolresult:hidden">{icon}</span>
            <span className="hidden group-hover/toolresult:block">
              <ChevronRightIcon />
            </span>
          </span>
        </span>{' '}
        {label}
      </button>
    </NodeViewWrapper>
  )
}

// Component to render AI Tool Result node
export const AiToolResultNodeView: React.FC<NodeViewProps> = ({ node }) => {
  const result = node.textContent || 'No result'

  if (node.attrs.toolName === 'suggestContentAndOrSchemaChanges') {
    let parsedResult: null | {
      status: string
      message: string
      suggestion?: { branchId: string; description?: string }
    } = null
    try {
      parsedResult = JSON.parse(node.textContent) as any
    } catch (err) {
      // noop
    }
    if (parsedResult) {
      return (
        <NodeViewWrapper>
          <ErrorBoundary
            fallback={
              <LightSuggestion
                status={parsedResult.status}
                message={parsedResult.message}
                branchId={parsedResult.suggestion?.branchId}
                description={parsedResult.suggestion?.description}
              />
            }
          >
            <Suggestion
              status={parsedResult.status}
              message={parsedResult.message}
              branchId={parsedResult.suggestion?.branchId}
              description={parsedResult.suggestion?.description}
            />
          </ErrorBoundary>
        </NodeViewWrapper>
      )
    }
  } else if (node.attrs.toolName === 'createImage') {
    let imageURL = null
    try {
      const parsed = JSON.parse(result) as string
      imageURL = new URL(parsed)
    } catch (err) {
      // noop
    }

    if (imageURL) {
      return (
        <NodeViewWrapper
          className={clsx(
            'text-xxs text-dark-faint hidden pt-1',
            `ai-tool-toggle-hidden`
          )}
        >
          <img
            src={imageURL.toString()}
            alt="Image generated by START"
            className="rounded"
          />
        </NodeViewWrapper>
      )
    }
  }
  return (
    <NodeViewWrapper
      className={clsx(
        'text-xxs text-dark-faint pl-5 pt-1 hidden',
        `ai-tool-toggle-hidden`
      )}
    >
      <ScrollArea asChild orientation="both">
        <pre
          data-tool-result
          className="max-h-64 overflow-auto rounded !bg-dark-base-dark border border-dark-shade-hover p-1 whitespace-pre"
        >
          {result}
        </pre>
      </ScrollArea>
    </NodeViewWrapper>
  )
}

// Component to render AI Error node
export const AiErrorNodeView: React.FC<NodeViewProps> = ({ node }) => {
  const errorMessage = node.textContent || 'Unknown error'

  return (
    <NodeViewWrapper className="mb-4">
      <div className="rounded-md border border-dark-error/80 bg-dark-error/10 overflow-hidden text-xxs">
        <div className="px-3 py-2 bg-dark-error-subtle border-b border-dark-error">
          <div className="flex items-center gap-2 text-sm font-medium text-dark-error">
            <Cross1Icon />
            <span>Error</span>
          </div>
        </div>
        <div className="px-3 py-2">
          <pre className="whitespace-pre-wrap !bg-inherit font-mono">
            {errorMessage}
          </pre>
        </div>
      </div>
    </NodeViewWrapper>
  )
}

// Extension for ai-tool node
export const AiTool = Node.create({
  name: 'aiTool',
  group: 'block',
  content: 'aiToolCall aiToolResult',

  addStorage() {
    return {
      expandResult: false,
    }
  },

  addAttributes() {
    return {
      toolId: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-ai-tool]',
        getAttrs: (dom) => ({
          toolId: (dom as HTMLElement).getAttribute('data-tool-id'),
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        ...HTMLAttributes,
        'data-ai-tool': true,
        'data-tool-id': HTMLAttributes.toolId,
      },
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AiToolNodeView)
  },
})

// Extension for ai-tool-call node
export const AiToolCall = Node.create({
  name: 'aiToolCall',
  group: 'block',
  content: 'inline*',

  addAttributes() {
    return {
      toolName: {
        default: null,
      },
      args: {
        default: {},
        parseHTML: (element) => {
          const argsAttr = element.getAttribute('data-args')
          return argsAttr ? JSON.parse(argsAttr) : {}
        },
        renderHTML: (attributes) => {
          return {
            'data-args': JSON.stringify(attributes.args),
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-ai-tool-call]',
        getAttrs: (dom) => ({
          toolName: (dom as HTMLElement).getAttribute('data-tool-name'),
          args: JSON.parse(
            (dom as HTMLElement).getAttribute('data-args') || '{}'
          ),
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        ...HTMLAttributes,
        'data-ai-tool-call': true,
        'data-tool-name': HTMLAttributes.toolName,
        'data-args':
          typeof HTMLAttributes.args === 'string'
            ? HTMLAttributes.args
            : JSON.stringify(HTMLAttributes.args),
      },
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AiToolCallNodeView)
  },
})

// Extension for ai-tool-result node
export const AiToolResult = Node.create({
  name: 'aiToolResult',
  group: 'block',
  content: 'paragraph+',

  addAttributes() {
    return {
      toolName: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-ai-tool-result]',
        getAttrs: (dom) => ({
          toolName: (dom as HTMLElement).getAttribute('data-tool-name'),
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        ...HTMLAttributes,
        'data-ai-tool-result': true,
        'data-tool-name': HTMLAttributes.toolName,
      },
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AiToolResultNodeView)
  },
})

// Extension for ai-error node
export const AiError = Node.create({
  name: 'aiError',
  group: 'block',
  content: 'text*',

  addAttributes() {
    return {}
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-ai-error]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        ...HTMLAttributes,
        'data-ai-error': true,
      },
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AiErrorNodeView)
  },
})

// Export all extensions together for easy importing
export const aiToolExtensions = [AiToolCall, AiToolResult, AiTool, AiError]
